import React, { useCallback, useEffect, useRef, useState } from "react";
import { Typography, Box, useMediaQuery, useTheme } from "@mui/material";
import { Button as Btn } from "@mui/material";
import { useStyles } from "./style";
import { withStyles } from "@mui/styles";
import { useDropzone } from "react-dropzone";
import LinearProgress from "@mui/material/LinearProgress";
import CloseIcon from "@mui/icons-material/Close";
import { connect } from "react-redux";
import { addAnswer, addImage } from "../../../../redux/survey/actions";
import uploadIcon from "../../../../assets/images/survey/UploadIcon.svg";
import UploadBtn from "../../../../assets/images/survey/UploadBtn.svg";
import { allowedMimeTypes } from "../../../helpers/mimeTypes";
import {
  completeFileProcessing,
  registerFileProcessing,
} from "../../../helpers/fileProcessing";
import { compressFiles } from "../../../../utils/compressFiles";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";

function Upload(props) {
  const intl = useIntl();
  const classes = useStyles(props);
  // Destructure props for easier access
  const { question, answeres, addAnswer_, addImage_, type, isGreyOut } = props;
  const [files, setFiles] = useState([]);
  const [processingFiles, setProcessingFiles] = useState([]);
  const pendingProcessingRef = useRef(0);
  const pendingSyncCompletionsRef = useRef(0);

  const createProcessingEntry = (file) => ({
    id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
    name: file.name,
    size: file.size,
    progress: 0,
  });

  const updateProcessingProgress = useCallback((index, progress, entryIds) => {
    const entryId = entryIds[index];
    if (!entryId) return;
    setProcessingFiles((curr) =>
      curr.map((entry) =>
        entry.id === entryId ? { ...entry, progress } : entry,
      ),
    );
  }, []);
  // Access theme
  const theme = useTheme();
  // Check if screen size is mobile
  const isMob = useMediaQuery(theme.breakpoints.down("sm"));

  /**
   * Reset the underlying file input so the same file can be reselected
   * after a validation error.
   */
  const clearFileInput = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  /**
   * Handle dropzone file selection.
   *
   * react-dropzone v14 expects `accept` as an object map; passing an array
   * silently disables filtering, so we validate every accepted file against
   * `allowedMimeTypes` here. If any file is invalid (or react-dropzone
   * already rejected files via `rejFiles`), surface a toast and clear the
   * input without mutating state.
   *
   * @param {File[]} accFiles - Files accepted by react-dropzone.
   * @param {Array} rejFiles - Files rejected by react-dropzone's `accept` rule.
   */
  const onDrop = useCallback(
    (accFiles, rejFiles) => {
      const validFiles = (accFiles || []).filter((file) =>
        allowedMimeTypes.includes(file.type),
      );
      const hasRejected =
        (rejFiles && rejFiles.length > 0) ||
        validFiles.length !== (accFiles || []).length;

      if (hasRejected) {
        toast.error(intl.formatMessage({ id: "please-select-a-valid-file" }));
        clearFileInput();
        if (validFiles.length === 0) {
          return;
        }
      }

      const droppedFiles = validFiles;
      const entries = droppedFiles.map(createProcessingEntry);
      const entryIds = entries.map((entry) => entry.id);
      setProcessingFiles((curr) => [...curr, ...entries]);
      pendingProcessingRef.current += 1;
      registerFileProcessing();

      compressFiles(droppedFiles, intl, {
        onProgress: (index, progress) =>
          updateProcessingProgress(index, progress, entryIds),
      })
        .then((compressedFiles) => {
          setProcessingFiles((curr) =>
            curr.filter((entry) => !entries.some((e) => e.id === entry.id)),
          );
          pendingSyncCompletionsRef.current += 1;
          setFiles((curr) => [...curr, ...compressedFiles]);
        })
        .catch(() => {
          setProcessingFiles((curr) =>
            curr.filter((entry) => !entries.some((e) => e.id === entry.id)),
          );
          if (pendingProcessingRef.current > 0) {
            pendingProcessingRef.current -= 1;
            completeFileProcessing();
          }
        });
    },
    [intl, updateProcessingProgress],
  );

  const { getRootProps, getInputProps, inputRef } = useDropzone({
    accept: allowedMimeTypes.reduce((acc, type) => {
      acc[type] = [];
      return acc;
    }, {}),
    onDrop,
  });

  const loadData = () => {
    let prevAnswer;
    if (type === "note") {
      prevAnswer = answeres[question._id] ?? { image: [] };
    } else {
      prevAnswer = answeres[question._id] ?? { value: [] };
    }
    let file =
      type === "note" ? (prevAnswer?.image ?? []) : (prevAnswer?.value ?? []);

    setFiles(file);
  };
  useEffect(() => {
    loadData();
  }, []);

  const setData = () => {
    if (type === "note") {
      addImage_({
        id: question._id,
        image: files,
        isFile: true,
      });
    } else {
      addAnswer_({
        id: question._id,
        value: files,
        isFile: true,
      });
    }
  };
  useEffect(() => {
    setData();
    const completions = pendingSyncCompletionsRef.current;
    if (completions > 0) {
      pendingSyncCompletionsRef.current = 0;
      for (let i = 0; i < completions; i++) {
        if (pendingProcessingRef.current > 0) {
          pendingProcessingRef.current -= 1;
          completeFileProcessing();
        }
      }
    }
  }, [files]);

  useEffect(() => {
    return () => {
      pendingSyncCompletionsRef.current = 0;
      while (pendingProcessingRef.current > 0) {
        pendingProcessingRef.current -= 1;
        completeFileProcessing();
      }
    };
  }, []);

  const BorderLinearProgress = withStyles((theme) => ({
    root: {
      width: "100%",
    },

    colorPrimary: {
      height: "7px",
      width: "100%",
      borderRadius: "17px",
      backgroundColor: "#069038",
      overflow: "hidden",
    },

    bar: {
      height: "7px",
      borderRadius: "17px",
    },
  }))(LinearProgress);
  function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  }

  const renderFileRow = (file, { isProcessing, onRemove }) => (
    <div key={file.id ?? file.name} className={classes.ProgressContainer}>
      <div style={{ flex: "1 1 auto", marginRight: "20px" }}>
        <div className={classes.progressDesc}>
          <Typography className={classes.progressDescText}>
            {file.name}
          </Typography>
          <Typography className={classes.progressDescText}>
            {formatBytes(file.size)}
          </Typography>
        </div>
        {isProcessing ? (
          <BorderLinearProgress
            variant="determinate"
            value={file.progress ?? 0}
          />
        ) : null}
      </div>
      {!isProcessing && onRemove ? (
        <CloseIcon
          style={{ cursor: "pointer" }}
          htmlColor="#99A5C5"
          onClick={onRemove}
        />
      ) : null}
    </div>
  );

  return (
    <>
      {/* <CssBaseline/> */}

      <Box
        style={{ cursor: isGreyOut && "not-allowed" }}
        className={classes.uploadBox}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <div className={classes.leftContent}>
          <img
            src={uploadIcon}
            alt="upload"
            className={classes.uploadBoxIcon}
          />
          <Typography className={classes.dragTitle}>
            {isMob
              ? intl.formatMessage({
                  id: "file-size-should-be-less-than-10-mb",
                })
              : intl.formatMessage({
                  id: "upload-or-drag-and-drop-your-files-here",
                })}
          </Typography>
        </div>
        <Btn
          variant="contained"
          className={classes.margin}
          disableElevation
          disableRipple
          style={{
            color: "#FFFFFF",
          }}
        >
          {intl.formatMessage({ id: "browse-files" })}
          <img src={UploadBtn} alt="upload" className={classes.uploadBtnIcon} />
        </Btn>
      </Box>

      {processingFiles.map((file) =>
        renderFileRow(file, { isProcessing: true }),
      )}

      {files.length
        ? files.map((filewrapper, i) =>
            renderFileRow(
              {
                id: `uploaded-${i}-${filewrapper.name}-${filewrapper.size}`,
                name: filewrapper.name,
                size: filewrapper.size,
              },
              {
                isProcessing: false,
                onRemove: () => {
                  setFiles((prev) => prev.filter((_, id) => id !== i));
                },
              },
            ),
          )
        : null}
    </>
  );
}
// Map Redux state to component props
const mapStateToProps = (state) => {
  return {
    answeres: state.survey.answeres,
  };
};
// Map Redux dispatch functions to component props
const mapDispatchToProps = (dispatch) => {
  return {
    addAnswer_: (data) => dispatch(addAnswer(data)),
    addImage_: (data) => dispatch(addImage(data)),
  };
};
// Connect component to Redux store
export default connect(mapStateToProps, mapDispatchToProps)(Upload);
